import type { DeskLang } from "@chonburi/shared";
import { LANG_LABEL, type DeskGap } from "../lib/mayorDesk";

/**
 * The census of what the desk covers — and of what it could not show.
 *
 * A desk that looks full while hiding a gap is the one thing this surface
 * may not do, so the empty languages and the untranslated headlines are
 * named here, in both languages, and the machine translations are counted
 * rather than passed off as the publisher's own words.
 */

interface Props {
  /** The languages the feed drew from. */
  languages: DeskLang[];
  /** How many headlines on the desk are machine translations. */
  machineCount: number;
  /** Everything the desk could not show. */
  gaps: DeskGap[];
  source: string;
  ageMinutes: number;
  /** True while this browser is still translating. */
  pending?: boolean;
}

export function DeskCensus({
  languages,
  machineCount,
  gaps,
  source,
  ageMinutes,
  pending,
}: Props) {
  return (
    <section className="desk-compartment desk-census" aria-label="ความครอบคลุม / Coverage census">
      <p className="desk-compartment-label">ความครอบคลุม · Coverage</p>
      <p className="desk-census-line">
        {languages.length
          ? `ครอบคลุม ${languages.map((l) => LANG_LABEL[l].th).join(" · ")}`
          : "—"}
        <span>
          {languages.length
            ? `${languages.length} languages · ${machineCount} headlines machine-translated`
            : "—"}
        </span>
      </p>
      {pending && (
        <p className="desk-census-line desk-census-line--clear">
          กำลังแปลข่าวจากภาษาอื่นในเครื่องนี้… · Translating the other languages in this browser
        </p>
      )}
      {gaps.length > 0 ? (
        <ul className="desk-gaps">
          {gaps.map((gap) => (
            <li key={gap.en}>
              <span>{gap.th}</span>
              <span className="desk-gap-en">{gap.en}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="desk-census-line desk-census-line--clear">
          ครอบคลุมครบทุกภาษาที่มีข่าว · Every language that had coverage is on this desk
        </p>
      )}
      <p className="desk-census-provenance mono">
        {source} · {ageMinutes} นาทีที่แล้ว · {ageMinutes} min ago
      </p>
    </section>
  );
}
